import { useCallback, useMemo, useState } from 'react';
import { useMutation } from '@apollo/client';
import UPDATE_REWARD_AS_DISMISSED from '@components/modal-manager/modals/nerdwallet-plus/footer-tracker/dismiss-reward-mutation';
import {
  FinancialBankingReward,
  FinancialCreditCardReward,
  FinancialNwPlusRewardProductEnum,
  NwPlusRewardStepStatus,
  InvestmentAccountNwPlusRewardStatus,
} from '@generated-gql/generated-types';
import { useQueryWithLog } from '@lib/hooks/logging-hooks';
import { financialPortalsQuery } from '@components/financial-institutions/plaid-sandbox/queries';
import { toastr } from 'react-redux-toastr';
import { getLogger } from '@lib/logging';
import { usePlaidModalVisibility } from '@components/financial-institutions/plaid-sandbox/plaid-modal-visibility-context';
import { PlaidStep } from '@components/financial-institutions/plaid-sandbox/plaid-modal-stepper';

const logger = getLogger('RewardsTracker');

const DISMISS_ERROR_MESSAGE =
  'There has been a problem dismissing your reward. Please try again.';

type FinancialReward = FinancialBankingReward | FinancialCreditCardReward;

export const getDefaultAnalyticsProps = (
  reward: FinancialReward,
): Record<string, any> => {
  const creditCard = reward as FinancialCreditCardReward;
  const banking = reward as FinancialBankingReward;

  const defaultSegmentTrackerProps = {
    [FinancialNwPlusRewardProductEnum.CreditCards]: {
      institution_id: creditCard.marketplaceCreditCardOffer?.institution?.id,
      institution_name:
        creditCard.marketplaceCreditCardOffer?.institution?.name,
      product_type: 'Credit Card',
    },
    [FinancialNwPlusRewardProductEnum.Banking]: {
      institution_id: banking.marketplaceBankingOffer?.institution?.id,
      institution_name: banking.marketplaceBankingOffer?.institution?.name,
      product_type: 'Banking',
    },
  }[reward.productType];

  return defaultSegmentTrackerProps;
};

export const useTrackerSteps = (
  reward: FinancialReward | InvestmentAccountNwPlusRewardStatus,
) => {
  const initialStep = useMemo(() => {
    const isFullyCompleted =
      reward.steps?.every(
        (step) => step.status === NwPlusRewardStepStatus.Completed,
      ) ?? false;

    if (isFullyCompleted) {
      return reward.steps?.length - 1;
    }

    return (
      reward.steps?.findIndex(
        (step) => step.status !== NwPlusRewardStepStatus.Completed,
      ) ?? 0
    );
  }, [reward]);

  const [selectedStep, setSelectedStep] = useState(initialStep);

  const currentStep = reward.steps?.[selectedStep];
  const amountOfSteps = reward.steps?.length ?? 0;
  const amountOfCompletedSteps = useMemo(() => {
    return (
      reward?.steps?.filter(
        (step) => step.status === NwPlusRewardStepStatus.Completed,
      )?.length ?? 0
    );
  }, [reward?.steps]);

  return {
    initialStep,
    selectedStep,
    setSelectedStep,
    currentStep,
    amountOfSteps,
    amountOfCompletedSteps,
  };
};

export const getOfferDisplayName = (
  reward: FinancialReward,
): string | undefined => {
  const displayName = {
    [FinancialNwPlusRewardProductEnum.CreditCards]: (
      reward as FinancialCreditCardReward
    )?.marketplaceCreditCardOffer?.displayName,
    [FinancialNwPlusRewardProductEnum.Banking]: (
      reward as FinancialBankingReward
    )?.marketplaceBankingOffer?.displayName,
  }[reward.productType];

  return displayName;
};

export const useActions = () => {
  const { setShowPlaidVisibility } = usePlaidModalVisibility();

  const { refetch: refetchPlaidLinkedAccounts } = useQueryWithLog(
    logger,
    financialPortalsQuery,
    {
      fetchPolicy: 'network-only',
    },
  );

  const [updateRewardAsDismissed] = useMutation(UPDATE_REWARD_AS_DISMISSED, {
    onError: () => {
      toastr.error(DISMISS_ERROR_MESSAGE, '');
    },
  });

  const handleOnLinkAccount = useCallback(() => {
    setShowPlaidVisibility({ show: true, plaidStep: PlaidStep.PLAID_LINK });
  }, [setShowPlaidVisibility]);

  return {
    refetchPlaidLinkedAccounts,
    updateRewardAsDismissed,
    handleOnLinkAccount,
  };
};
